'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function FactorCalculator() {
  const t = useTranslations("tools.factorCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [numberInput, setNumberInput] = useState('120');
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const isPrime = (n: number): boolean => {
    if (n < 2) return false;
    if (n === 2) return true;
    if (n % 2 === 0) return false;
    for (let i = 3; i <= Math.sqrt(n); i += 2) {
      if (n % i === 0) return false;
    }
    return true;
  };

  const findFactors = (n: number): number[] => {
    const factors: number[] = [];
    const sqrt = Math.sqrt(n);

    for (let i = 1; i <= sqrt; i++) {
      if (n % i === 0) {
        factors.push(i);
        if (i !== n / i) {
          factors.push(n / i);
        }
      }
    }

    return factors.sort((a, b) => a - b);
  };

  const primeFactorization = (n: number): number[] => {
    const factors: number[] = [];
    let num = n;

    while (num % 2 === 0) {
      factors.push(2);
      num = num / 2;
    }

    for (let i = 3; i <= Math.sqrt(num); i += 2) {
      while (num % i === 0) {
        factors.push(i);
        num = num / i;
      }
    }

    if (num > 2) {
      factors.push(num);
    }

    return factors;
  };

  const formatPrimeFactorization = (factors: number[]): string => {
    if (factors.length === 0) return '1';

    const counts: { [key: number]: number } = {};
    factors.forEach(f => {
      counts[f] = (counts[f] || 0) + 1;
    });

    const parts: string[] = [];
    for (let prime in counts) {
      if (counts[prime] === 1) {
        parts.push(prime);
      } else {
        parts.push(prime + '^' + counts[prime]);
      }
    }

    return parts.join(' × ');
  };

  const getFactorPairs = (factors: number[]): [number, number][] => {
    const pairs: [number, number][] = [];
    const len = factors.length;
    const mid = Math.ceil(len / 2);

    for (let i = 0; i < mid; i++) {
      pairs.push([factors[i], factors[len - 1 - i]]);
    }

    return pairs;
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const value = parseInt(numberInput);

    if (isNaN(value) || value < 1) {
      setError(t('errorPositive'));
      setResults(null);
      return;
    }

    if (value > 1000000000) {
      setError(t('errorTooLarge'));
      setResults(null);
      return;
    }

    const factors = findFactors(value);
    const primeFactors = primeFactorization(value);
    const factorPairs = getFactorPairs(factors);

    let numberType = t('typeComposite');
    if (value === 1) {
      numberType = t('typeUnity');
    } else if (isPrime(value)) {
      numberType = t('typePrime');
    }

    const primeFactorizationText = value === 1
      ? t('noPrimeFactors')
      : formatPrimeFactorization(primeFactors);

    setResults({
      number: value,
      totalFactors: factors.length,
      numberType,
      factors,
      primeFactorization: primeFactorizationText,
      factorPairs
    });
  };

  const handleClear = () => {
    setNumberInput('');
    setResults(null);
    setError('');
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    { marginBottom: '1.5rem' },
    { marginBottom: '2rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto', background: 'white', borderRadius: '1rem', overflow: 'hidden' },
    { maxWidth: '100%' },
    { maxWidth: '600px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700 },
    { fontSize: '1.5rem' },
    { fontSize: '1.65rem' },
    { fontSize: '1.8rem' }
  );

  const formStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = {
    marginBottom: '1.5rem'
  };

  const labelStyle = getResponsiveStyle(
    { display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#1e293b' },
    { fontSize: '0.9rem' },
    { fontSize: '0.925rem' },
    { fontSize: '0.95rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
      fontSize: '1rem'
    },
    { padding: '0.75rem' },
    { padding: '0.8125rem' },
    { padding: '0.875rem' }
  );

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', marginTop: '1.5rem' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' }
  );

  const clearBtnStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' }
  );

  const errorMessageStyle = {
    background: '#fee2e2',
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
    borderLeft: '4px solid #dc2626',
    display: error ? 'block' : 'none'
  };

  const infoBoxStyle = getResponsiveStyle(
    {
      background: '#eff6ff',
      borderLeft: '4px solid #2563eb',
      padding: '1rem',
      borderRadius: '0.5rem',
      marginTop: '1rem',
      color: '#1e40af'
    },
    { fontSize: '0.85rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.9rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      borderTop: '3px solid #f59e0b',
      display: results ? 'block' : 'none'
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { margin: '0 0 1.5rem 0', color: '#92400e', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.2rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.3rem' }
  );

  const resultSectionStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.75rem',
      marginBottom: '1.5rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
    },
    { padding: '1.25rem' },
    { padding: '1.375rem' },
    { padding: '1.5rem' }
  );

  const resultSectionH4Style = getResponsiveStyle(
    {
      margin: '0 0 1rem 0',
      color: '#1e293b',
      borderBottom: '2px solid #e2e8f0',
      paddingBottom: '0.5rem'
    },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const resultItemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 0',
    borderBottom: '1px solid #f1f5f9'
  };

  const resultLabelStyle = {
    fontWeight: 600,
    color: '#475569'
  };

  const resultValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#2563eb' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const factorsGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '0.75rem', marginTop: '1rem' },
    { gridTemplateColumns: 'repeat(auto-fill, minmax(50px, 1fr))', gap: '0.5rem' },
    { gridTemplateColumns: 'repeat(auto-fill, minmax(55px, 1fr))', gap: '0.625rem' },
    { gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))', gap: '0.75rem' }
  );

  const factorBadgeStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      padding: '0.5rem',
      borderRadius: '0.5rem',
      textAlign: 'center' as const,
      fontWeight: 600,
      boxShadow: '0 2px 6px rgba(37, 99, 235, 0.2)'
    },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const primeFactorizationStyle = {
    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
    padding: '1rem',
    borderRadius: '0.5rem',
    borderLeft: '4px solid #22c55e',
    marginTop: '1rem'
  };

  const primeFactorizationTextStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#166534', fontFamily: "'Courier New', monospace" },
    { fontSize: '1.1rem' },
    { fontSize: '1.15rem' },
    { fontSize: '1.2rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const listItemStyle = {
    marginBottom: '0.75rem'
  };

  const strongStyle = {
    color: colors.textPrimary,
    fontWeight: 600
  };

  const faqItemStyle = {
    marginBottom: '1.5rem'
  };

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.2rem' },
    { fontSize: '1.3rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const refListStyle = {
    listStyle: 'none',
    padding: 0
  };

  const refItemStyle = {
    marginBottom: '1rem'
  };

  const refLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-word' as const
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>{t('toolTitle')}</h2>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label htmlFor="number-input" style={labelStyle}>{t('inputLabel')}</label>
              <input
                type="number"
                id="number-input"
                min="1"
                max="1000000000"
                step="1"
                placeholder={t('inputPlaceholder')}
                value={numberInput}
                onChange={(e) => setNumberInput(e.target.value)}
                style={inputStyle}
                required
              />
            </div>

            {error && (
              <div style={errorMessageStyle}>
                {error}
              </div>
            )}

            <div style={buttonGroupStyle}>
              <button type="submit" style={calculateBtnStyle}>
                {t('calculateButton')}
              </button>
              <button type="button" style={clearBtnStyle} onClick={handleClear}>
                {t('clearButton')}
              </button>
            </div>

            <div style={infoBoxStyle}>
              💡 {t('infoText')}
            </div>
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>📊 {t('resultsTitle')}</h3>

              <div style={resultSectionStyle}>
                <h4 style={resultSectionH4Style}>{t('numberInfoTitle')}</h4>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('numberLabel')}</span>
                  <span style={resultValueStyle}>{results.number.toLocaleString()}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('totalFactorsLabel')}</span>
                  <span style={resultValueStyle}>{results.totalFactors}</span>
                </div>
                <div style={{ ...resultItemStyle, borderBottom: 'none' }}>
                  <span style={resultLabelStyle}>{t('typeLabel')}</span>
                  <span style={resultValueStyle}>{results.numberType}</span>
                </div>
              </div>

              <div style={resultSectionStyle}>
                <h4 style={resultSectionH4Style}>{t('allFactorsTitle')}</h4>
                <div style={factorsGridStyle}>
                  {results.factors.map((factor: number, index: number) => (
                    <div key={index} style={factorBadgeStyle}>
                      {factor}
                    </div>
                  ))}
                </div>
              </div>

              <div style={resultSectionStyle}>
                <h4 style={resultSectionH4Style}>{t('primeFactorizationTitle')}</h4>
                <div style={primeFactorizationStyle}>
                  <div style={primeFactorizationTextStyle}>{results.primeFactorization}</div>
                </div>
              </div>

              <div style={{ ...resultSectionStyle, marginBottom: 0 }}>
                <h4 style={resultSectionH4Style}>{t('factorPairsTitle')}</h4>
                {results.factorPairs.map((pair: [number, number], index: number) => (
                  <div key={index} style={{ ...resultItemStyle, borderBottom: index === results.factorPairs.length - 1 ? 'none' : '1px solid #f1f5f9' }}>
                    <span style={resultLabelStyle}>{pair[0]} × {pair[1]}</span>
                    <span style={resultValueStyle}>{results.number.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step1Title')}</strong> {t('step1Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step2Title')}</strong> {t('step2Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step3Title')}</strong> {t('step3Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step4Title')}</strong> {t('step4Description')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insight1')}</p>
        <p style={paragraphStyle}>{t('insight2')}</p>
        <p style={paragraphStyle}>{t('insight3')}</p>
        <p style={paragraphStyle}>{t('insight4')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem' }}>{t('whatAreFactorsTitle')}</h3>
        <p style={paragraphStyle}>{t('whatAreFactorsText')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem' }}>{t('propertiesTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('property1')}</li>
          <li style={listItemStyle}>{t('property2')}</li>
          <li style={listItemStyle}>{t('property3')}</li>
          <li style={listItemStyle}>{t('property4')}</li>
        </ul>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem' }}>{t('primeFactorizationDetailsTitle')}</h3>
        <p style={paragraphStyle}>{t('primeFactorizationDetailsText')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem' }}>{t('applicationsTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('application1')}</li>
          <li style={listItemStyle}>{t('application2')}</li>
          <li style={listItemStyle}>{t('application3')}</li>
          <li style={listItemStyle}>{t('application4')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq1Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq1Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq2Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq2Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq3Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq3Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq4Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq4Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq5Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq5Answer')}</p>
        </div>

        <div style={{ ...faqItemStyle, marginBottom: 0 }}>
          <h3 style={faqQuestionStyle}>{t('faq6Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq6Answer')}</p>
        </div>
      </section>

      <section style={{ ...glassCardStyle, marginBottom: 0 }}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={refListStyle}>
          <li style={refItemStyle}>
            <a href="https://www.splashlearn.com/math-vocabulary/multiplication/factor" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref1')}
            </a>
          </li>
          <li style={refItemStyle}>
            <a href="https://www.basic-mathematics.com/factoring-in-math.html" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref2')}
            </a>
          </li>
          <li style={refItemStyle}>
            <a href="https://www.omnicalculator.com/math/factor" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref3')}
            </a>
          </li>
          <li style={refItemStyle}>
            <a href="https://www.britannica.com/science/factor-mathematics" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref4')}
            </a>
          </li>
          <li style={refItemStyle}>
            <a href="https://brighterly.com/math/factor/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref5')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}